import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import { NotificationTypeEnum } from '../../enums';
import { LocalizationService } from '../localization';
import { NotificationExtraData } from '../../models';
import {
  NotificationSnackBarComponent,
  NotificationSnackBarData,
} from '../../helper-modules/notification-snackbar/notification-snackbar.component';

type TrackedNotification = {
  id: string;
  type: NotificationTypeEnum;
  message: string;
  snackBarRef: MatSnackBarRef<NotificationSnackBarComponent>;
};

const PANEL_CLASS_MAP: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.SUCCESS]: 'snackbar-success',
  [NotificationTypeEnum.ERROR]: 'snackbar-error',
  [NotificationTypeEnum.INFO]: 'snackbar-info',
  [NotificationTypeEnum.DEFAULT]: 'snackbar-default',
};

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  // TODO: MatSnackBar is single-instance and replaces the previous message on open.
  // Previous angular-notifier behavior supported stacking (up to 4 concurrent notifications).
  // If stacked notifications are required again, implement custom multi-toast handling via CDK Overlay.
  private snackBar = inject(MatSnackBar);
  private localization = inject(LocalizationService);

  private readonly autoHide = 3000;
  private trackedNotifications = new Set<TrackedNotification>();

  notify(
    type: NotificationTypeEnum,
    message: string,
    interpolateParams: Object = {},
    extraData: NotificationExtraData = {},
  ): void {
    const translatedMessage: string = this.localization.translateInstant(
      message,
      interpolateParams,
    );

    if (translatedMessage) {
      const { autoHide = this.autoHide } = extraData;
      const id = extraData?.id || Math.random().toString();

      if (autoHide) {
        this.showAndAutoHide({ type, message: translatedMessage, id }, autoHide);
      } else {
        this.show({ type, message: translatedMessage, id });
      }
    }
  }

  private openSnackBar(
    data: NotificationSnackBarData,
    panelClass: string,
    duration?: number,
  ): MatSnackBarRef<NotificationSnackBarComponent> {
    return this.snackBar.openFromComponent(NotificationSnackBarComponent, {
      data,
      duration,
      panelClass: [panelClass],
      verticalPosition: 'top',
      horizontalPosition: 'end',
    });
  }

  private show(options: { type: NotificationTypeEnum; message: string; id: string }): void {
    const panelClass = PANEL_CLASS_MAP[options.type] || 'snackbar-default';
    this.openSnackBar({ message: options.message, type: options.type }, panelClass);
  }

  private showAndAutoHide(
    options: { type: NotificationTypeEnum; message: string; id: string },
    delayMs: number,
  ): void {
    const { type, id, message } = options;

    const alreadyShown = this.getTrackedNotification({ message });
    if (alreadyShown) {
      // Dismiss the existing snackbar and let it reopen below to reset the timer.
      alreadyShown.snackBarRef.dismiss();
    }

    const panelClass = PANEL_CLASS_MAP[type] || 'snackbar-default';
    const ref = this.openSnackBar({ message, type }, panelClass, delayMs);

    const tracked: TrackedNotification = { id, type, message, snackBarRef: ref };
    this.trackedNotifications.add(tracked);

    ref.afterDismissed().subscribe(() => {
      this.trackedNotifications.delete(tracked);
    });
  }

  private getTrackedNotification(selector: { id?: string; message?: string }): TrackedNotification {
    const { id, message } = selector;
    const trackedNotifications = Array.from(this.trackedNotifications.values());

    return trackedNotifications.find((notification: TrackedNotification) => {
      const idPass = id === undefined || notification.id === id;
      const messagePass = message === undefined || notification.message === message;

      return idPass && messagePass;
    });
  }
}
